import React, { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Images } from 'lucide-react';
import { fetchPublicWallEntries } from '../services/textureShowcase';

const DESKTOP_PREVIEW_LIMIT = 5;
const MOBILE_PREVIEW_LIMIT = 5;
const PREVIEW_LIMIT_CAP = DESKTOP_PREVIEW_LIMIT;
const HOME_WALL_CACHE_KEY = 'colobongo_home_wall_preview_cache_v5';
const HOME_WALL_CACHE_AT_KEY = 'colobongo_home_wall_preview_cache_at_v5';
const HOME_WALL_CACHE_TTL_MS = 20 * 60 * 1000;
const MOBILE_MEDIA_QUERY = '(max-width: 639px)';
const STACKED_WALL_PREVIEW_LAYOUTS = [
  {
    role: 'hero',
    top: '6%',
    left: '5%',
    width: '50%',
    imageAspect: '1 / 1',
    rotate: -2.1,
    z: 50,
    float: 2.4,
  },
  {
    role: 'topRight',
    top: '6%',
    left: '53%',
    width: '40%',
    imageAspect: '4 / 3',
    rotate: 2.2,
    z: 38,
    float: 2,
  },
  {
    role: 'bottomRight',
    top: '41%',
    left: '51%',
    width: '41%',
    imageAspect: '4 / 3',
    rotate: -1.4,
    z: 40,
    float: 2.2,
  },
  {
    role: 'bottomLeft',
    top: '51%',
    left: '9%',
    width: '39%',
    imageAspect: '4 / 3',
    rotate: 1.6,
    z: 32,
    float: 1.8,
  },
  {
    role: 'centerPeek',
    top: '29%',
    left: '36%',
    width: '34%',
    imageAspect: '1 / 1',
    rotate: 3,
    z: 24,
    float: 1.5,
  },
];

const previewRequestCache = new Map();

function getHomePreviewLimit(isMobile) {
  return isMobile ? MOBILE_PREVIEW_LIMIT : DESKTOP_PREVIEW_LIMIT;
}

function getInitialIsMobile() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
}

function sanitizePreviewEntries(entries = []) {
  return (Array.isArray(entries) ? entries : [])
    .map((entry) => ({
      id: entry?.id || null,
      artwork_title: entry?.artwork_title || null,
      showcase_name: entry?.showcase_name || null,
      image_url: entry?.image_url || null,
      image_display_url: entry?.image_display_url || null,
      full_display_url: entry?.full_display_url || null,
      home_display_url: entry?.home_display_url || null,
      thumbnail_display_url: entry?.thumbnail_display_url || null,
      image_path: entry?.image_path || null,
      submitted_by_contributor: Boolean(entry?.submitted_by_contributor),
      created_at: entry?.created_at || null,
      wall_approved_at: entry?.wall_approved_at || null,
    }))
    .filter((entry) => entry.id && (entry.home_display_url || entry.full_display_url || entry.image_url));
}

function getHomeArtworkImageUrl(entry) {
  return entry?.home_display_url || entry?.full_display_url || entry?.image_url || null;
}

function readHomeWallPreviewCache(limit = PREVIEW_LIMIT_CAP) {
  if (typeof window === 'undefined') return [];

  try {
    const cacheAt = Number(window.sessionStorage.getItem(HOME_WALL_CACHE_AT_KEY) || 0);
    const cacheRaw = window.sessionStorage.getItem(HOME_WALL_CACHE_KEY);

    if (!cacheAt || !cacheRaw || Date.now() - cacheAt > HOME_WALL_CACHE_TTL_MS) {
      window.sessionStorage.removeItem(HOME_WALL_CACHE_KEY);
      window.sessionStorage.removeItem(HOME_WALL_CACHE_AT_KEY);
      return [];
    }

    const parsed = JSON.parse(cacheRaw);
    return sanitizePreviewEntries(parsed).slice(0, limit);
  } catch {
    return [];
  }
}

function writeHomeWallPreviewCache(entries = []) {
  if (typeof window === 'undefined') return;

  try {
    const sanitized = sanitizePreviewEntries(entries).slice(0, PREVIEW_LIMIT_CAP);
    window.sessionStorage.setItem(HOME_WALL_CACHE_KEY, JSON.stringify(sanitized));
    window.sessionStorage.setItem(HOME_WALL_CACHE_AT_KEY, String(Date.now()));
  } catch {
    // noop
  }
}

async function requestHomeWallPreview(limit) {
  const normalizedLimit = Math.min(Math.max(Number(limit) || DESKTOP_PREVIEW_LIMIT, 1), PREVIEW_LIMIT_CAP);
  const requestKey = `wall-preview:${normalizedLimit}`;

  if (previewRequestCache.has(requestKey)) {
    return previewRequestCache.get(requestKey);
  }

  const requestPromise = fetchPublicWallEntries({
    limit: normalizedLimit,
    section: 'all',
    preview: true,
  })
    .then((rows) => {
      const sanitized = sanitizePreviewEntries(rows).slice(0, normalizedLimit);
      writeHomeWallPreviewCache(sanitized);
      return sanitized;
    })
    .finally(() => {
      previewRequestCache.delete(requestKey);
    });

  previewRequestCache.set(requestKey, requestPromise);
  return requestPromise;
}

export async function prefetchHomeWallPreview({ limit = DESKTOP_PREVIEW_LIMIT } = {}) {
  try {
    return await requestHomeWallPreview(limit);
  } catch {
    return [];
  }
}

function buildAlt(entry, language) {
  const artist = entry?.showcase_name || (language === 'fr' ? 'artiste ColoBongo' : 'ColoBongo artist');
  const title = String(entry?.artwork_title || '').trim();
  return title
    ? (language === 'fr' ? `${title}, par ${artist}` : `${title}, by ${artist}`)
    : (language === 'fr' ? `Coloriage partagé par ${artist}` : `Coloring shared by ${artist}`);
}

function PreviewSkeletonCards() {
  return (
    <div className="relative mt-4 h-[320px] sm:h-[340px] overflow-hidden rounded-[24px] bg-[linear-gradient(180deg,rgba(255,255,255,0.88),rgba(255,247,240,0.95))] p-3">
      {STACKED_WALL_PREVIEW_LAYOUTS.map((layout, index) => (
        <div
          key={`wall-skeleton-${index}`}
          className="absolute bg-white shadow-[0_12px_26px_rgba(61,61,61,0.12)] animate-pulse"
          style={{
            top: layout.top,
            left: layout.left,
            width: layout.width,
            zIndex: layout.z,
            transform: `rotate(${layout.rotate}deg)`,
            borderRadius: 3,
            padding: '6px 6px 18px 6px',
          }}
        >
          <div
            className="bg-[linear-gradient(180deg,rgba(255,244,236,0.95),rgba(245,232,223,0.95))]"
            style={{ aspectRatio: layout.imageAspect || '1 / 1' }}
          />
          <div className="space-y-2 px-1 pt-2">
            <div className="h-3 w-4/5 rounded-full bg-[rgba(61,61,61,0.08)]" />
            <div className="h-2 w-1/2 rounded-full bg-[rgba(61,61,61,0.06)]" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function HomeArtworkShowcase({ language = 'fr', embedded = false }) {
  const shouldReduceMotion = useReducedMotion();
  const initialMobile = getInitialIsMobile();
  const initialEntries = readHomeWallPreviewCache(getHomePreviewLimit(initialMobile));
  const [isMobile, setIsMobile] = useState(initialMobile);
  const [entries, setEntries] = useState(initialEntries);
  const [isLoading, setIsLoading] = useState(initialEntries.length === 0);
  const [failedImageIds, setFailedImageIds] = useState({});

  const maxVisibleEntries = getHomePreviewLimit(isMobile);
  const previewEntries = useMemo(
    () => sanitizePreviewEntries(entries).slice(0, maxVisibleEntries),
    [entries, maxVisibleEntries]
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const updateViewport = (event) => {
      setIsMobile(Boolean(event?.matches));
    };

    setIsMobile(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateViewport);
      return () => mediaQuery.removeEventListener('change', updateViewport);
    }

    mediaQuery.addListener(updateViewport);
    return () => mediaQuery.removeListener(updateViewport);
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!previewEntries.length) {
      setIsLoading(true);
    }

    requestHomeWallPreview(maxVisibleEntries)
      .then((rows) => {
        if (cancelled) return;
        setEntries(rows);
      })
      .catch(() => {
        if (cancelled) return;
        if (!previewEntries.length) {
          setEntries([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [maxVisibleEntries]);

  useEffect(() => {
    const visibleIds = new Set(previewEntries.map((entry) => String(entry.id)));
    setFailedImageIds((prev) => Object.fromEntries(
      Object.entries(prev).filter(([entryId]) => visibleIds.has(entryId))
    ));
  }, [previewEntries]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;

    previewEntries.forEach((entry) => {
      // eslint-disable-next-line no-console
      console.info('[HOME_WALL_IMAGE_SOURCE]', {
        title: entry?.artwork_title || null,
        hasHome: Boolean(entry?.home_display_url),
        hasFull: Boolean(entry?.full_display_url),
        hasImageDisplay: Boolean(entry?.image_display_url),
        hasImageUrl: Boolean(entry?.image_url),
        chosen: getHomeArtworkImageUrl(entry),
      });
    });
  }, [previewEntries]);

  const sectionClassName = embedded
    ? 'overflow-hidden'
    : 'cozy-card p-4 bg-[rgba(255,252,249,0.96)] border border-[rgba(232,168,124,0.12)] overflow-hidden';

  const handleImageError = (entryId) => {
    const normalizedId = String(entryId);
    setFailedImageIds((prev) => (prev[normalizedId] ? prev : { ...prev, [normalizedId]: true }));
  };

  return (
    <section className={sectionClassName}>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[rgba(232,168,124,0.16)] text-[color:var(--cb-primary)] flex items-center justify-center flex-shrink-0">
          <Images size={20} />
        </div>
        <div className="min-w-0">
          <p className="cozy-section-kicker mb-1">{language === 'fr' ? 'Inspiration partagée' : 'Shared inspiration'}</p>
          <h2 className="text-xl font-extrabold text-[color:var(--cb-text)] leading-tight">
            {language === 'fr' ? 'Le Mur de la Colomunauté' : 'The Colomunauty Wall'}
          </h2>
          <p className="text-sm text-[color:var(--cb-text-muted)] mt-1 leading-relaxed">
            {language === 'fr'
              ? 'Les derniers coloriages partagés par la Colomunauté.'
              : 'The most recent colorings shared by the Colomunauty.'}
          </p>
        </div>
      </div>

      {isLoading && previewEntries.length === 0 ? (
        <PreviewSkeletonCards />
      ) : previewEntries.length > 0 ? (
        <div className="relative mt-4 h-[320px] sm:h-[340px] overflow-hidden rounded-[24px] bg-[linear-gradient(180deg,rgba(255,255,255,0.88),rgba(255,247,240,0.95))] p-3">
            {previewEntries.slice(0, 5).map((entry, index) => {
              const title = String(entry?.artwork_title || '').trim();
              const showcaseName = String(entry?.showcase_name || '').trim();
              const displayUrl = getHomeArtworkImageUrl(entry);
              const hasImageFailed = Boolean(failedImageIds[String(entry.id)]) || !displayUrl;
              const layout = STACKED_WALL_PREVIEW_LAYOUTS[index];
              const isHero = layout.role === 'hero';
              const isPeek = layout.role === 'centerPeek';
              const showMeta = layout.role !== 'centerPeek';

              return (
                <motion.div
                  key={entry.id}
                  className="absolute min-w-0"
                  style={{
                    top: layout.top,
                    left: layout.left,
                    width: layout.width,
                    zIndex: layout.z,
                    transformOrigin: isHero ? '42% 62%' : '50% 50%',
                  }}
                  initial={{ opacity: 0, scale: 0.97, rotate: layout.rotate }}
                  animate={shouldReduceMotion
                    ? { opacity: 1, scale: 1, rotate: layout.rotate }
                    : {
                      opacity: 1,
                      scale: 1,
                      y: [0, -layout.float, 0, layout.float * 0.45, 0],
                      rotate: [
                        layout.rotate,
                        layout.rotate + 0.35,
                        layout.rotate,
                        layout.rotate - 0.25,
                        layout.rotate,
                      ],
                    }}
                  transition={shouldReduceMotion
                    ? {
                      opacity: { duration: 0.22, delay: index * 0.04 },
                      scale: { duration: 0.22, delay: index * 0.04 },
                      rotate: { duration: 0.22, delay: index * 0.04 },
                    }
                    : {
                      opacity: { duration: 0.22, delay: index * 0.04 },
                      scale: { duration: 0.22, delay: index * 0.04 },
                      y: {
                        duration: 6.5 + index * 0.6,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: index * 0.2,
                      },
                      rotate: {
                        duration: 7.5 + index * 0.6,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: index * 0.2,
                      },
                    }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Link
                    to="/mur"
                    aria-label={buildAlt(entry, language)}
                    className={`group block bg-white transition-transform transition-shadow ${
                      isHero
                        ? 'shadow-[0_18px_38px_rgba(61,61,61,0.17)]'
                        : isPeek
                          ? 'shadow-[0_8px_18px_rgba(61,61,61,0.09)]'
                          : 'shadow-[0_12px_26px_rgba(61,61,61,0.12)]'
                    } md:hover:-translate-y-0.5 md:hover:shadow-[0_16px_30px_rgba(61,61,61,0.16)]`}
                    style={{
                      borderRadius: 3,
                      padding: showMeta ? '6px 6px 18px 6px' : '6px',
                    }}
                  >
                    <div
                      className="relative overflow-hidden"
                      style={{ aspectRatio: layout.imageAspect || '1 / 1', background: 'var(--color-surface)' }}
                    >
                      {hasImageFailed ? (
                        <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-3 text-center">
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-[color:var(--cb-primary)]">
                            <Images size={16} />
                          </div>
                          <p className="text-[10px] font-semibold leading-tight text-[color:var(--cb-text-muted)]">
                            {language === 'fr' ? 'Image indisponible' : 'Image unavailable'}
                          </p>
                        </div>
                      ) : (
                        <img
                          src={displayUrl}
                          alt={buildAlt(entry, language)}
                          className="h-full w-full object-cover object-center"
                          loading={index < 2 ? 'eager' : 'lazy'}
                          decoding="async"
                          onError={() => handleImageError(entry.id)}
                        />
                      )}
                    </div>
                    {showMeta && (
                      <div className="px-1 pt-1.5">
                        <p
                          className="truncate leading-tight"
                          style={{
                            fontFamily: 'Caveat, cursive',
                            fontSize: isHero ? 16 : 14,
                            fontWeight: 700,
                            color: 'var(--color-text-primary)',
                          }}
                        >
                          {title || (language === 'fr' ? 'Sans titre' : 'Untitled')}
                        </p>
                        <p
                          className="truncate mt-0.5"
                          style={{
                            fontSize: isHero ? 10 : 9,
                            color: 'var(--color-text-tertiary)',
                            fontWeight: 500,
                          }}
                        >
                          {showcaseName || (language === 'fr' ? 'Colomunauté' : 'Colomunauty')}
                        </p>
                      </div>
                    )}
                  </Link>
                </motion.div>
              );
            })}
        </div>
      ) : (
        <div className="mt-4 h-[248px] sm:h-[280px] flex items-center justify-center rounded-[24px] bg-[linear-gradient(180deg,rgba(255,255,255,0.82),rgba(255,248,242,0.94))]">
          <p className="text-[12px] text-[color:var(--cb-text-muted)] text-center px-6">
            {language === 'fr'
              ? 'Aucun coloriage disponible pour le moment.'
              : 'No colorings available right now.'}
          </p>
        </div>
      )}

      <div className="mt-3 text-center">
        <Link
          to="/mur"
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[color:var(--cb-primary)] hover:underline"
        >
          {language === 'fr' ? 'Voir le Mur complet' : 'View the full Wall'}
          <span aria-hidden="true">→</span>
        </Link>
      </div>
    </section>
  );
}
